import { useEffect, useRef, useState } from 'react';
import { GeoClass } from '../../../interface/response/GeoFiltersResponse';
import { useAppSelector } from '../../../hook/useAppSelector';
import { sortByName } from '../../../util/sort';

interface SearchProps {
  data?: any[];
  searchKeys?: string[];
  onFilteredData?: (filtered: GeoClass[]) => void;
  placeholder?: string;
  className?: string;
  autoFocus?: boolean;
}

/**
 * Generic search component used across filter dropdowns.
 * Search behavior:
 *  - Filters items based on the provided search keys.
 *  - Search is case-insensitive.
 *  - Items whose value starts with the entered query are displayed first.
 *  - Items that only contain the query within the value are displayed afterwards.
 *  - Items within the same priority group are sorted alphabetically.
 *  - When the search query is empty, the original data order is preserved.
 *
 * Example:
 * Query: "test"
 *
 * Priority 1:
 *  Test
 *  Test Company
 *  Testing
 *
 * Priority 2:
 *  Another Test
 *  My Test
 *
 * This component only controls filtering and ordering of search results.
 * The consumer is responsible for rendering the filtered data.
 */

const Search: React.FC<SearchProps> = ({
  data,
  searchKeys,
  onFilteredData,
  placeholder,
  className,
  autoFocus = false,
}) => {
  const [query, setQuery] = useState('');
  const searchKeysRef = useRef(searchKeys);
  const searchResetCount = useAppSelector((state) => state.dateFilter.searchResetCount);

  useEffect(() => {
    setQuery('');
  }, [searchResetCount]);

  useEffect(() => {
    searchKeysRef.current = searchKeys;
  }, [searchKeys]);

  useEffect(() => {
    if (!Array.isArray(data)) {
      onFilteredData?.([]);
      return;
    }

    const normalizedQuery = query.trim().toLowerCase();
    const keys = searchKeysRef.current ?? [];

    if (!normalizedQuery) {
      onFilteredData?.(sortByName(data));
      return;
    }

    const filteredData = data
      .filter((item) =>
        keys.some((key) => {
          const value = String(item[key] ?? '').toLowerCase();

          return value.includes(normalizedQuery);
        }),
      )
      .sort((a, b) => {
        const getSearchValue = (item: any): string => {
          return (
            keys
              .map((key) =>
                String(item[key] ?? '')
                  .trim()
                  .toLowerCase(),
              )
              .find((value) => value.includes(normalizedQuery)) ?? ''
          );
        };

        const aValue = getSearchValue(a);
        const bValue = getSearchValue(b);

        const aStartsWith = aValue.startsWith(normalizedQuery);
        const bStartsWith = bValue.startsWith(normalizedQuery);

        if (aStartsWith && !bStartsWith) {
          return -1;
        }

        if (!aStartsWith && bStartsWith) {
          return 1;
        }

        return aValue.localeCompare(bValue, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      });

    onFilteredData?.(filteredData);
  }, [query, data, onFilteredData]);

  return (
    <div className="search_wrapper">
      <input
        type="text"
        placeholder={placeholder}
        className={`search_input ${className}`}
        name="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        autoFocus={autoFocus}
      />
      {query && (
        <span className="clear_search" onClick={() => setQuery('')}>
          &times;
        </span>
      )}
    </div>
  );
};

export default Search;
